<script lang="ts">
	import { CirclePlus, CircleMinus } from 'lucide-svelte';
	import type { GraphNode } from '$lib/tree/types';
	import type { EffectSummary, EffectSource } from '$lib/tree/effects-calculator';
	import { calculateCurrentEffects, formatEffectDescription } from '$lib/tree/effects-calculator';

	interface Props {
		selectedNodes: string[];
		nodes: Record<string, GraphNode>;
		icons: Record<string, string>;
		timelineStep?: number;
		onClear?: () => void;
		onHighlightNodes?: (nodeIds: string[]) => void;
		onClearHighlight?: () => void;
	}

	let { selectedNodes, nodes, icons, timelineStep = 0, onClear, onHighlightNodes, onClearHighlight }: Props = $props();

	// Progressive: only show effects from nodes up to the current timeline step
	const visibleNodes = $derived(
		timelineStep > 0 ? selectedNodes.slice(0, timelineStep) : selectedNodes
	);

	const effects = $derived(calculateCurrentEffects(visibleNodes, nodes));

	let hoveredEffect: string | null = $state(null);
	let hoveredNodeId: string | null = $state(null);
	let flashedEffects = $state(new Set<string>());
	let previousTimelineStep: number | null = null;
	let previousEffectSignatures = new Map<string, string>();
	let flashTimeout: ReturnType<typeof setTimeout> | null = null;

	function getEffectSignature(effect: EffectSummary): string {
		return JSON.stringify({
			totalValue: effect.totalValue,
			values: effect.values,
			sources: effect.sources.map((source) => [source.nodeId, source.value, source.isSecondary, source.secondaryName ?? ''])
		});
	}

	$effect(() => {
		const currentStep = timelineStep;
		const currentEffects = effects;
		const currentSignatures = new Map(currentEffects.map((effect) => [effect.effectName, getEffectSignature(effect)]));

		if (previousTimelineStep !== null && previousTimelineStep !== currentStep) {
			const changedEffects = new Set<string>();
			for (const effect of currentEffects) {
				if (previousEffectSignatures.get(effect.effectName) !== currentSignatures.get(effect.effectName)) {
					changedEffects.add(effect.effectName);
				}
			}

			flashedEffects = changedEffects;
			if (flashTimeout) clearTimeout(flashTimeout);
			if (changedEffects.size > 0) {
				flashTimeout = setTimeout(() => {
					flashedEffects = new Set();
					flashTimeout = null;
				}, 700);
			}
		}

		previousTimelineStep = currentStep;
		previousEffectSignatures = currentSignatures;

		return () => {
			if (flashTimeout) {
				clearTimeout(flashTimeout);
				flashTimeout = null;
			}
		};
	});

	function getIconUrl(nodeId: string): string | undefined {
		const node = nodes[nodeId];
		return node?.iconActive || icons[nodeId];
	}

	function getTypeColor(type: string | undefined): string {
		switch (type) {
			case 'ranged': return '#22c55e';
			case 'melee': return '#ef4444';
			case 'magic': return '#3b82f6';
			case 'regeneration': return '#84cc16';
			default: return '#9ca3af';
		}
	}

	function getSizeBadgeColor(size: string): string {
		switch (size) {
			case 'node_capstone': return 'var(--color-accent-primary)';
			case 'node_major': return '#ef4444';
			default: return 'var(--color-text-muted)';
		}
	}

	function handleEffectMouseEnter(effect: EffectSummary) {
		hoveredEffect = effect.effectName;
		const sourceIds = effect.sources.map(s => s.nodeId);
		onHighlightNodes?.(sourceIds);
	}

	function handleEffectMouseLeave() {
		hoveredEffect = null;
		onClearHighlight?.();
	}

	function handleNodeMouseEnter(nodeId: string) {
		hoveredNodeId = nodeId;
		onHighlightNodes?.([nodeId]);
	}

	function handleNodeMouseLeave() {
		hoveredNodeId = null;
		if (hoveredEffect) {
			const effect = effects.find(e => e.effectName === hoveredEffect);
			if (effect) {
				onHighlightNodes?.(effect.sources.map(s => s.nodeId));
			}
		} else {
			onClearHighlight?.();
		}
	}

	function getFormattedTitle(effect: EffectSummary): string {
		const { title } = formatEffectDescription(effect.displayName, effect.totalValue);
		return title;
	}

	function getFormattedBody(effect: EffectSummary): string {
		const { body } = formatEffectDescription(effect.displayName, effect.totalValue);
		if (!body) return '';
		// Convert <col=*> tags and replace # with total
		let formatted = body.replace(/#/g, String(effect.totalValue));
		formatted = formatted.replace(/<col=([^>]+)>([^<]*)<\/col>/g, '<span class="colored-text" style="color: $1;">$2</span>');
		return formatted;
	}

	// Format node label for icon tooltip, replacing # with the source's actual value
	function getNodeTooltip(node: GraphNode, source: EffectSource): string {
		const label = node.label?.split('\n\n')[0] || node.id;
		// Replace # with the actual value from this source
		const formatted = label.replace(/#/g, String(source.value));
		// Strip HTML tags for plain text tooltip
		return formatted.replace(/<col=[^>]*>|<\/col>/g, '');
	}

	function groupSourcesByNode(sources: EffectSource[]): EffectSource[] {
		const grouped = new Map<string, EffectSource>();
		for (const source of sources) {
			if (!grouped.has(source.nodeId)) {
				grouped.set(source.nodeId, { ...source });
			}
		}
		return Array.from(grouped.values());
	}
</script>

<div class="sidebar">
	<div class="sidebar-body">
		{#if selectedNodes.length === 0}
			<div class="empty-state">
				<CirclePlus size={48} stroke-width={1.5} class="empty-icon" />
				<p class="empty-text">No pacts selected</p>
				<p class="empty-hint">Select pacts to see combined effects</p>
			</div>
		{:else if effects.length === 0}
			<div class="empty-state">
				<CircleMinus size={48} stroke-width={1.5} class="empty-icon" />
				<p class="empty-text">No effects</p>
				<p class="empty-hint">Selected pacts have no summary cards</p>
			</div>
		{:else}
			<div class="effects-list">
				{#each effects as effect}
					{@const body = getFormattedBody(effect)}
					<div 
						class="effect-card"
						class:hovered={hoveredEffect === effect.effectName}
						class:flashed={flashedEffects.has(effect.effectName)}
						onmouseenter={() => handleEffectMouseEnter(effect)}
						onmouseleave={handleEffectMouseLeave}
						role="button"
						tabindex="0"
					>
						<div class="effect-content">
							<div class="effect-title">{@html getFormattedTitle(effect)}</div>
							{#if body}
								<div class="effect-body">{@html body}</div>
							{/if}
							<div class="effect-footer">
								<div class="effect-sources">
									{#each groupSourcesByNode(effect.sources) as source}
										{@const node = nodes[source.nodeId]}
										{@const iconUrl = getIconUrl(source.nodeId)}
										{#if node}
											<div 
												class="source-node"
												class:hovered={hoveredNodeId === source.nodeId}
												onmouseenter={() => handleNodeMouseEnter(source.nodeId)}
												onmouseleave={handleNodeMouseLeave}
												title="{getNodeTooltip(node, source)}{source.isSecondary ? ' (secondary)' : ''}"
												role="button"
												tabindex="0"
											>
												<div 
													class="source-icon"
													style="--badge-color: {getSizeBadgeColor(node.size || 'node_minor')}; --type-color: {getTypeColor(node.type)}"
												>
													{#if iconUrl}
														<img src={iconUrl} alt="" />
													{:else}
														<div class="icon-placeholder"></div>
													{/if}
													{#if source.isSecondary}
														<span class="secondary-badge">2°</span>
													{/if}
												</div>
											</div>
										{/if}
									{/each}
								</div>
							</div>
						</div>
					</div>
				{/each}
			</div>
		{/if}
	</div>
</div>

<style>
	.sidebar {
		position: relative;
		height: 100%;
		width: 100%;
		background-color: var(--color-bg-surface);
		background-image: radial-gradient(circle, var(--color-bg-grid-dot-soft) 1px, transparent 1px);
		background-size: 20px 20px;
		border-left: 1px solid var(--color-border-muted);
		display: flex;
		flex-direction: column;
		z-index: 1000;
	}

	.sidebar-body {
		flex: 1;
		overflow-y: auto;
		padding: 12px;
	}

	.sidebar-body::-webkit-scrollbar {
		width: 5px;
	}

	.sidebar-body::-webkit-scrollbar-track {
		background: transparent;
	}

	.sidebar-body::-webkit-scrollbar-thumb {
		background: rgba(75, 85, 99, 0.4);
		border-radius: 3px;
	}

	.empty-state {
		display: flex;
		flex-direction: column;
		align-items: center;
		justify-content: center;
		padding: 40px 16px;
		text-align: center;
	}

	.empty-icon {
		width: 40px;
		height: 40px;
		color: #3f3f46;
		margin-bottom: 12px;
	}

	.empty-text {
		margin: 0 0 4px;
		font-size: 13px;
		font-weight: 500;
		color: var(--color-text-tertiary);
	}

	.empty-hint {
		margin: 0;
		font-size: 12px;
		color: var(--color-text-muted);
	}

	.effects-list {
		display: flex;
		flex-direction: column;
		gap: 6px;
	}

	.effect-card {
		background: var(--color-bg-subtle);
		border: 1px solid var(--color-border-soft);
		border-radius: 8px;
		padding: 10px 12px;
		transition: all 0.15s ease;
		cursor: pointer;
	}

	.effect-card:hover,
	.effect-card.hovered {
		background: var(--color-bg-panel-hover);
		border-color: color-mix(in srgb, var(--color-accent-primary) 25%, transparent);
	}

	.effect-card.flashed {
		animation: effect-flash 700ms ease-out;
	}

	@keyframes effect-flash {
		0% {
			background: var(--color-accent-primary-soft);
			border-color: color-mix(in srgb, var(--color-accent-primary) 60%, transparent);
			box-shadow: 0 0 0 0 color-mix(in srgb, var(--color-accent-primary) 24%, transparent);
		}
		35% {
			background: color-mix(in srgb, var(--color-accent-primary) 10%, transparent);
			border-color: color-mix(in srgb, var(--color-accent-primary) 38%, transparent);
			box-shadow: 0 0 0 6px color-mix(in srgb, var(--color-accent-primary) 10%, transparent);
		}
		100% {
			background: var(--color-bg-subtle);
			border-color: var(--color-border-soft);
			box-shadow: 0 0 0 0 transparent;
		}
	}

	.effect-content {
		display: flex;
		flex-direction: column;
		gap: 6px;
	}

	.effect-title {
		font-size: 12px;
		font-weight: 500;
		color: var(--color-text-primary);
		line-height: 1.5;
	}

	.effect-title :global(.colored-text) {
		color: var(--color-accent-primary);
		font-weight: 600;
	}

	.effect-body {
		font-size: 11px;
		line-height: 1.5;
		color: var(--color-text-tertiary);
		padding-left: 10px;
		border-left: 2px solid var(--color-accent-primary-soft);
	}

	.effect-body :global(.colored-text) {
		color: var(--color-accent-primary);
		font-weight: 600;
	}

	.effect-footer {
		display: flex;
		align-items: center;
		gap: 8px;
		margin-top: 4px;
	}

	.effect-sources {
		display: flex;
		flex-wrap: wrap;
		gap: 4px;
		justify-content: flex-start;
	}

	.source-node {
		display: flex;
		align-items: center;
		cursor: pointer;
		padding: 2px;
		border-radius: 4px;
		transition: all 0.15s ease;
	}

	.source-node:hover,
	.source-node.hovered {
		background: var(--color-bg-subtle-strong);
	}

	.source-icon {
		position: relative;
		width: 22px;
		height: 22px;
		border-radius: 3px;
		overflow: hidden;
		background: rgba(0, 0, 0, 0.3);
		border: 1.5px solid var(--badge-color, #6b7280);
	}

	.source-icon img {
		width: 100%;
		height: 100%;
		object-fit: contain;
		image-rendering: pixelated;
	}

	.icon-placeholder {
		width: 100%;
		height: 100%;
		background: var(--color-border-soft);
	}

	.secondary-badge {
		position: absolute;
		bottom: -1px;
		right: -1px;
		background: var(--color-accent-primary);
		color: var(--color-text-inverse);
		font-size: 7px;
		font-weight: 700;
		padding: 0px 2px;
		border-radius: 2px;
		line-height: 1.2;
	}

	@media (max-width: 768px) {
		/* Mobile drawer context - fill parent container */
		.sidebar {
			position: relative;
			top: auto;
			bottom: auto;
			left: auto;
			right: auto;
			width: 100%;
			height: 100%;
			max-height: none;
			border-left: none;
			border-top: none;
			border-radius: 0;
		}

		.sidebar-body {
			padding: 12px;
			height: 100%;
		}
	}
</style>
